(() => {
  const version = "sample-concierge-v1-2026-08-29"
  const disclosure = "By tapping “Yes, text me,” you agree to recurring automated DTFSheet texts about your sample and occasional offers. Msg frequency varies. Msg & data rates may apply. Texts aren’t required to order. Reply STOP to opt out or HELP for help."
  const create = ({ enabled = false, captureEnabled = false, companyId = "VmfH8w", listId = "XKCTan", form, button, phoneInput, disclosureElement, readPhone = () => phoneInput?.value.trim(), bridge, fetchImpl = window.fetch.bind(window), now = Date.now } = {}) => {
    let state = "idle"
    let action = null
    let controller = null
    let sequence = 0
    let retryAt = 0
    let providerAttempted = false
    let settling = false
    const consumedClaims = new Set()
    const consumedEvents = new WeakSet()
    const configured = () => enabled === true && captureEnabled === true && companyId === "VmfH8w" && listId === "XKCTan" && ["claim", "record", "cancel", "reconcile"].every(key => typeof bridge?.[key] === "function")
    const result = (extra = {}) => Object.freeze({ state, providerAttempted, retryAt, ...extra })
    const update = (next, extra) => { state = next; return result(extra) }
    const validGrant = grant => grant && grant.sessionId === action.sessionId && grant.actionId === action.actionId && grant.phone === action.phone && grant.disclosureVersion === version && grant.companyId === companyId && grant.listId === listId && typeof grant.attemptId === "string" && grant.attemptId.length >= 16 && typeof grant.claimId === "string" && grant.claimId.length >= 16 && Number.isFinite(grant.expiresAt) && grant.expiresAt > now() && grant.expiresAt <= action.startedAt + 10000
    const record = async outcome => {
      settling = true
      try {
        if (controller.signal.aborted) return false
        const receipt = await Promise.race([
          bridge.record({ sessionId: action.sessionId, actionId: action.actionId, attemptId: action.attemptId, claimId: action.claimId, outcome }),
          new Promise(resolve => controller.signal.addEventListener("abort", () => resolve(null), { once: true }))
        ])
        return receipt?.ok === true
      } catch {
        return false
      } finally {
        settling = false
      }
    }
    const cancel = async () => {
      if (state === "cancelled") return result()
      sequence += 1
      controller?.abort()
      update("cancelled")
      let cancellationConfirmed = !action
      if (typeof bridge?.cancel === "function") {
        try {
          const receipt = await bridge.cancel({ sessionId: action?.sessionId, actionId: action?.actionId, attemptId: action?.attemptId || null })
          cancellationConfirmed = receipt?.ok === true && receipt?.cancelled === true
        } catch {
          cancellationConfirmed = false
        }
      }
      return result({ cancellationConfirmed, providerMayHaveAccepted: providerAttempted })
    }
    const reconcile = async () => {
      if (!action || ["cancelled", "claiming", "submitting", "needs-keyword"].includes(state) || !configured()) return result()
      const token = sequence
      try {
        const receipt = await bridge.reconcile({ sessionId: action.sessionId, actionId: action.actionId, attemptId: action.attemptId || null })
        if (state === "cancelled" || token !== sequence) return result()
        if (receipt?.cancelled === true) return cancel()
        if (receipt?.confirmed === true && receipt?.phoneBound === true && receipt?.attemptId === action.attemptId && receipt?.accountId === companyId) return update("confirmed")
        return result({ reconciliationPending: true })
      } catch {
        return result({ reconciliationPending: true })
      }
    }
    const submit = async (event, { sessionId } = {}) => {
      if (!configured()) return update("disabled")
      if (settling || ["cancelled", "claiming", "submitting", "pending", "unknown", "confirmed", "needs-keyword"].includes(state)) return result()
      if (now() < retryAt) return result()
      const fresh = event instanceof SubmitEvent && event.isTrusted && event.target === form && event.submitter === button && button?.isConnected && button.form === form && !button.disabled && navigator.userActivation?.isActive && document.visibilityState === "visible" && !consumedEvents.has(event)
      if (!fresh) return result({ refused: "fresh_customer_action_required" })
      event.preventDefault()
      consumedEvents.add(event)
      if (disclosureElement?.dataset.consentVersion !== version || disclosureElement.textContent.trim() !== disclosure || !disclosureElement.isConnected) return result({ refused: "current_disclosure_required" })
      const phone = readPhone()
      if (!/^\+1[2-9][0-9]{2}[2-9][0-9]{6}$/.test(phone || "") || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sessionId || "")) return result({ refused: "valid_phone_and_session_required" })
      const token = ++sequence
      action = { sessionId, phone, disclosureVersion: version, companyId, listId, actionId: crypto.randomUUID(), startedAt: now() }
      controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 10000)
      update("claiming")
      try {
        const responseGrant = await Promise.race([
          bridge.claim({ ...action, signal: controller.signal }),
          new Promise((resolve, reject) => controller.signal.addEventListener("abort", () => reject(new DOMException("Cancelled", "AbortError")), { once: true }))
        ])
        if (token !== sequence || state === "cancelled") return result()
        if (controller.signal.aborted || !navigator.userActivation?.isActive || document.visibilityState !== "visible" || now() - action.startedAt >= 10000 || readPhone() !== phone) return cancel()
        const grant = responseGrant && Object.freeze({ sessionId: responseGrant.sessionId, actionId: responseGrant.actionId, phone: responseGrant.phone, disclosureVersion: responseGrant.disclosureVersion, companyId: responseGrant.companyId, listId: responseGrant.listId, attemptId: responseGrant.attemptId, claimId: responseGrant.claimId, expiresAt: responseGrant.expiresAt, decision: responseGrant.decision, nativeProof: responseGrant.nativeProof && Object.freeze({ ...responseGrant.nativeProof }) })
        if (!validGrant(grant)) return update("blocked", { refused: "durable_bound_claim_required" })
        action.attemptId = grant.attemptId
        action.claimId = grant.claimId
        if (grant.decision === "cancelled") return cancel()
        if (grant.decision === "needs_keyword") return update("needs-keyword", { message: "If you previously sent STOP, text START or UNSTOP to the verified DTFSheet sending number. You can continue without SMS." })
        if (grant.decision === "already_subscribed") {
          const proof = grant.nativeProof
          if (proof?.phone !== phone || proof?.accountId !== companyId || proof?.consent !== "SUBSCRIBED" || proof?.canReceiveMarketing !== true || !Number.isFinite(proof.observedAt) || proof.observedAt > now() || now() - proof.observedAt > 30000) return update("blocked")
          return update("confirmed", { existingSubscriber: true })
        }
        if (grant.decision !== "submit_once" || consumedClaims.has(grant.claimId)) return update("blocked")
        consumedClaims.add(grant.claimId)
        update("submitting")
        providerAttempted = true
        const response = await fetchImpl(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`, {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          cache: "no-store",
          redirect: "error",
          referrerPolicy: "no-referrer",
          headers: { revision: "2026-07-15", "Content-Type": "application/vnd.api+json", Accept: "application/vnd.api+json" },
          body: JSON.stringify({ data: { type: "subscription", attributes: { custom_source: "DTFSheet free sample optional SMS v1", profile: { data: { type: "profile", attributes: { phone_number: phone, subscriptions: { sms: { marketing: { consent: "SUBSCRIBED" } } } } } } }, relationships: { list: { data: { type: "list", id: listId } } } } }),
          signal: controller.signal
        })
        if (token !== sequence || state === "cancelled") return result({ providerMayHaveAccepted: true })
        if (response.status === 202) {
          update("pending")
          const recorded = await record("accepted_pending_confirmation")
          if (token !== sequence || state === "cancelled") return result({ providerMayHaveAccepted: true })
          return result({ recorded, message: "Check your texts to confirm. You can continue without subscribing." })
        }
        if (response.status === 429) {
          const raw = response.headers.get("Retry-After")
          const delay = raw && /^\d+$/.test(raw) ? Number(raw) * 1000 : Date.parse(raw || "") - now()
          retryAt = now() + Math.max(1000, Number.isFinite(delay) ? delay : 60000)
          update("throttled")
          const recorded = await record("throttled")
          if (token !== sequence || state === "cancelled") return result({ providerMayHaveAccepted: true })
          return result({ recorded, freshActionRequired: true })
        }
        const next = [400, 401, 403, 404, 422].includes(response.status) ? "rejected" : "unknown"
        update(next)
        return result({ recorded: await record(next), reconciliationRequired: next === "unknown" })
      } catch {
        if (token !== sequence || state === "cancelled") return result({ providerMayHaveAccepted: providerAttempted })
        update("unknown")
        return result({ recorded: action.attemptId ? await record("unknown") : false, reconciliationRequired: true })
      } finally {
        clearTimeout(timeout)
      }
    }
    const leave = () => { void cancel() }
    const visibility = () => { if (document.visibilityState !== "visible") leave() }
    window.addEventListener("pagehide", leave)
    document.addEventListener("visibilitychange", visibility)
    return Object.freeze({ submit, cancel, reconcile, handoff: () => { window.removeEventListener("pagehide", leave); document.removeEventListener("visibilitychange", visibility) }, rearm: () => { window.removeEventListener("pagehide", leave); document.removeEventListener("visibilitychange", visibility); window.addEventListener("pagehide", leave); document.addEventListener("visibilitychange", visibility) }, status: () => result(), dispose: () => { leave(); window.removeEventListener("pagehide", leave); document.removeEventListener("visibilitychange", visibility) } })
  }
  window.DtfsSampleSmsClient = Object.freeze({ create, disclosureVersion: version, disclosure })
})()
